import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, ArrowRight } from "lucide-react";
import { PageHeader, Btn, Field, inputCls, Modal, Card } from "@/components/cc";
import StatusBadge from "@/components/StatusBadge";
import VerdictBadge from "@/components/VerdictBadge";

export default function Claims() {
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ statement: "", made_by: "", context: "", project_id: "" });

  const load = () => {
    api.get("/claims").then((r) => setItems(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/claims", { ...form, project_id: form.project_id || null });
      toast.success("Claim logged");
      setOpen(false);
      setForm({ statement: "", made_by: "", context: "", project_id: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="p-6">
      <PageHeader title="Claims & Evidence" subtitle="Fact-checking engine. A claim can cite many sources and evidence items.">
        <Btn data-testid="new-claim-btn" onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> Log Claim</Btn>
      </PageHeader>

      <div className="space-y-2" data-testid="claims-list">
        {items.length === 0 && <p className="text-sm text-zinc-600">No claims yet.</p>}
        {items.map((c) => (
          <Card key={c.id} className="p-4 flex items-start gap-4 hover:border-zinc-700 transition-colors">
            <div className="flex-1">
              <p className="font-medium text-zinc-100">{c.statement}</p>
              <div className="mt-1 text-xs text-zinc-500">
                {c.made_by && <>Claimed by {c.made_by} · </>}
                {c.source_ids?.length || 0} sources · {c.evidence_count || 0} evidence
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <StatusBadge status={c.status} />
              {c.verdict && <VerdictBadge verdict={c.verdict} />}
              <Link to={`/command-center/claims/${c.id}`} data-testid={`open-claim-${c.id}`} className="text-sm text-emerald-400 inline-flex items-center gap-1">
                Verify <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </Card>
        ))}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Log a Claim">
        <form onSubmit={create} className="space-y-4">
          <Field label="Claim statement"><textarea data-testid="claim-statement-input" className={inputCls} rows={3} required value={form.statement} onChange={(e) => setForm({ ...form, statement: e.target.value })} /></Field>
          <Field label="Made by"><input className={inputCls} value={form.made_by} onChange={(e) => setForm({ ...form, made_by: e.target.value })} placeholder="Person / organization" /></Field>
          <Field label="Context"><input className={inputCls} value={form.context} onChange={(e) => setForm({ ...form, context: e.target.value })} /></Field>
          <Field label="Project (optional)">
            <select className={inputCls} value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })}>
              <option value="">— none —</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
          </Field>
          <Btn data-testid="claim-save-btn" type="submit" className="w-full justify-center">Save Claim</Btn>
        </form>
      </Modal>
    </div>
  );
}
